import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  CircleNotch,
  Eye,
  EyeSlash,
  LockKey,
  ShieldCheck,
  WarningCircle,
} from "@phosphor-icons/react";
import { usePasswordToggle } from "@reactive-resume/hooks";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { motion } from "framer-motion";
import { z } from "zod";

import { useClaimAccount, validateClaim } from "@/client/services/campaigns/provisioning";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  PAGE PUBLIQUE D'ACTIVATION (/client/auth/activate?token=…)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Les anciens utilisateurs migrés depuis un CSV reçoivent un lien à usage
 *  unique (30 jours) pour définir leur mot de passe. Même design que la page
 *  « Nouveau mot de passe » : validate → formulaire (force + confirmation) →
 *  complete → AUTO-LOGIN (cookies posés par le serveur) → espace utilisateur.
 * ─────────────────────────────────────────────────────────────────────────────
 */

type Phase = "checking" | "invalid" | "ready" | "done";

const getActivateSchema = () =>
  z
    .object({
      password: z.string().min(8, { message: t`Minimum 8 caractères` }),
      confirm: z.string(),
    })
    .refine((values) => values.password === values.confirm, {
      path: ["confirm"],
      message: t`Les mots de passe ne correspondent pas`,
    });

type FormValues = z.infer<ReturnType<typeof getActivateSchema>>;

const ease = [0.22, 1, 0.36, 1] as const;
const fadeUp = (i: number) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: i * 0.06, duration: 0.4, ease },
});

/** Force du mot de passe — 0 (très faible) → 5 (excellent). */
const passwordScore = (password: string): number => {
  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;
  return score;
};

const SCORE_COLORS = [
  "bg-red-500",
  "bg-red-400",
  "bg-amber-400",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-emerald-600",
];

export const ActivatePage = () => {
  const scoreLabels = [t`Très faible`, t`Faible`, t`Correct`, t`Bon`, t`Solide`, t`Excellent`];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [phase, setPhase] = useState<Phase>("checking");
  const [name, setName] = useState<string | null>(null);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { claim, loading, error } = useClaimAccount();

  const formRef = useRef<HTMLFormElement>(null);
  usePasswordToggle(formRef);

  const form = useForm<FormValues>({
    resolver: zodResolver(getActivateSchema()),
    defaultValues: { password: "", confirm: "" },
  });

  // Force du mot de passe (useWatch = hook, appelé inconditionnellement).
  const passwordValue = useWatch({ control: form.control, name: "password" });
  const score = passwordScore(passwordValue ?? "");

  // ── Vérification du lien d'activation ──────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setPhase("invalid");
      return;
    }
    let cancelled = false;
    validateClaim(token)
      .then((result) => {
        if (cancelled) return;
        if (result.valid) {
          setName(result.name ?? null);
          setPhase("ready");
        } else {
          setPhase("invalid");
        }
      })
      .catch(() => {
        if (!cancelled) setPhase("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // ── Soumission : activation + auto-login (redirection par le hook) ─────────
  const onSubmit = async (data: FormValues) => {
    try {
      await claim({ token, password: data.password });
      setPhase("done");
    } catch {
      form.setValue("password", "");
      form.setValue("confirm", "");
    }
  };

  /* ── Phase 1 : vérification du lien ── */
  if (phase === "checking") {
    return (
      <>
        <Helmet>
          <title>{t`Activation du compte`} — Mon CV Pro</title>
        </Helmet>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center gap-4 py-12 text-center"
        >
          <CircleNotch size={30} className="animate-spin text-[#472cb3]" />
          <p className="text-[0.84rem] text-gray-400">{t`Vérification de votre lien d'activation…`}</p>
        </motion.div>
      </>
    );
  }

  /* ─ Phase 2 : lien invalide ou expiré ── */
  if (phase === "invalid") {
    return (
      <>
        <Helmet>
          <title>{t`Lien invalide`} — Mon CV Pro</title>
        </Helmet>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease }}
          className="flex flex-col items-center gap-5 py-6 text-center"
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-400/15">
            <WarningCircle size={34} weight="fill" className="text-amber-500" />
          </div>
          <div className="space-y-1.5">
            <h2 className="text-[1.5rem] font-extrabold leading-[1.2] tracking-[-0.02em] text-gray-900">
              {t`Lien d'activation invalide ou expiré`}
            </h2>
            <p className="text-[0.85rem] leading-relaxed text-gray-400">
              {t`Ce lien a peut-être déjà été utilisé ou a dépassé sa validité. Pas d'inquiétude : votre compte existe toujours, vous pouvez en recevoir un nouveau.`}
            </p>
          </div>
          <motion.div
            whileHover={{ scale: 1.012 }}
            whileTap={{ scale: 0.988 }}
            className="w-full pt-1"
          >
            <Button
              type="button"
              onClick={() => void navigate("/client/auth/forgot-password")}
              className="group flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#472cb3] text-[0.92rem] font-semibold text-white shadow-[0_4px_16px_rgba(71,44,179,0.3)] transition-all duration-200 hover:bg-[#3a22a0]"
            >
              {t`Recevoir un nouveau lien`}
              <ArrowRight
                size={16}
                weight="bold"
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Button>
          </motion.div>
          <button
            type="button"
            onClick={() => void navigate("/client/auth/login")}
            className="inline-flex items-center gap-1.5 text-[0.82rem] font-medium text-gray-400 transition-colors hover:text-[#472cb3]"
          >
            <ArrowLeft size={14} />
            {t`Retour à la connexion`}
          </button>
        </motion.div>
      </>
    );
  }

  /* ── Phase 3 : succès (l'auto-login redirige vers l'espace utilisateur) ─ */
  if (phase === "done") {
    return (
      <>
        <Helmet>
          <title>{t`Compte activé`} — Mon CV Pro</title>
        </Helmet>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease }}
          className="flex flex-col items-center gap-5 py-6 text-center"
        >
          <div className="bg-[#3ecf8e]/12 flex h-16 w-16 items-center justify-center rounded-full">
            <CheckCircle size={34} weight="fill" className="text-[#3ecf8e]" />
          </div>
          <div className="space-y-1.5">
            <h2 className="text-[1.5rem] font-extrabold leading-[1.2] tracking-[-0.02em] text-gray-900">
              {t`Votre compte est activé !`}
            </h2>
            <p className="text-[0.85rem] leading-relaxed text-gray-400">
              {t`Connexion en cours… vous allez être redirigé vers votre espace.`}
            </p>
          </div>
          <CircleNotch size={22} className="animate-spin text-[#472cb3]" />
        </motion.div>
      </>
    );
  }

  /* ── Phase 4 : formulaire d'activation (design « Nouveau mot de passe ») ─ */
  return (
    <>
      <Helmet>
        <title>{t`Activer mon compte`} — Mon CV Pro</title>
      </Helmet>

      {/* Eyebrow */}
      <motion.p
        {...fadeUp(0)}
        className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.2em] text-[#3ecf8e]"
      >
        MON CV PRO
      </motion.p>

      {/* Titre */}
      <motion.h1
        {...fadeUp(1)}
        className="mb-1 text-[1.85rem] font-extrabold leading-[1.18] tracking-[-0.025em] text-gray-900"
      >
        {t`Activez votre compte`}
      </motion.h1>

      {/* Sous-titre — accueil personnalisé si le nom est connu */}
      <motion.p {...fadeUp(2)} className="mb-6 text-[0.84rem] leading-relaxed text-gray-400">
        {name
          ? t`Bienvenue ${name} ! Dernière étape : choisissez votre mot de passe pour accéder à votre espace Mon CV Pro.`
          : t`Bienvenue ! Dernière étape : choisissez un mot de passe fort et sécurisé pour activer votre compte.`}
      </motion.p>

      <Form {...form}>
        <form
          ref={formRef}
          className="flex flex-col gap-y-3.5"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          {/* Nouveau mot de passe */}
          <motion.div {...fadeUp(3)}>
            <FormField
              name="password"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-[0.78rem] font-semibold text-gray-600">
                    {t`Mot de passe`}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <LockKey
                        size={16}
                        className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <Input
                        type={showPwd ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder={t`Minimum 8 caractères`}
                        className="h-11 rounded-xl border border-gray-200 bg-gray-50/60 pl-10 pr-11 text-sm placeholder:text-gray-300 focus-visible:border-[#472cb3] focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#472cb3]/15"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                        tabIndex={-1}
                        aria-label={showPwd ? "Masquer" : "Afficher"}
                      >
                        {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </motion.div>

          {/* Indicateur de force */}
          {passwordValue?.length > 0 && (
            <motion.div {...fadeUp(3.5)} className="space-y-1.5">
              <div className="flex gap-1.5">
                {[0, 1, 2, 3, 4].map((index) => (
                  <div
                    key={index}
                    className={cn(
                      "h-1.5 flex-1 rounded-full transition-colors duration-300",
                      index < score ? SCORE_COLORS[score] : "bg-gray-200",
                    )}
                  />
                ))}
              </div>
              <p className="text-[0.72rem] text-gray-400">
                {t`Force du mot de passe`} :{" "}
                <span className="font-semibold">{scoreLabels[score]}</span>
              </p>
            </motion.div>
          )}

          {/* Confirmation */}
          <motion.div {...fadeUp(4)}>
            <FormField
              name="confirm"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-[0.78rem] font-semibold text-gray-600">
                    {t`Confirmer le mot de passe`}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <ShieldCheck
                        size={16}
                        className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <Input
                        type={showConfirm ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder={t`Ressaisissez le mot de passe`}
                        className="h-11 rounded-xl border border-gray-200 bg-gray-50/60 pl-10 pr-11 text-sm placeholder:text-gray-300 focus-visible:border-[#472cb3] focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#472cb3]/15"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm((v) => !v)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                        tabIndex={-1}
                        aria-label={showConfirm ? "Masquer" : "Afficher"}
                      >
                        {showConfirm ? <EyeSlash size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </motion.div>

          {error && (
            <motion.div {...fadeUp(4.5)}>
              <p className="rounded-xl border border-red-200 bg-red-50/80 px-3.5 py-2.5 text-[0.78rem] leading-relaxed text-red-600">
                {t`Impossible d'activer votre compte. Ce lien est peut-être expiré ou déjà utilisé. Demandez un nouveau lien depuis la page « Mot de passe oublié ».`}
              </p>
            </motion.div>
          )}

          <motion.div {...fadeUp(5)} className="pt-0.5">
            <motion.div whileHover={{ scale: 1.012 }} whileTap={{ scale: 0.988 }}>
              <Button
                type="submit"
                disabled={loading}
                className="group flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#472cb3] text-[0.92rem] font-semibold text-white shadow-[0_4px_16px_rgba(71,44,179,0.3)] transition-all duration-200 hover:bg-[#3a22a0]"
              >
                {loading ? (
                  <>
                    <CircleNotch size={16} className="animate-spin" />
                    {t`Activation…`}
                  </>
                ) : (
                  <>
                    {t`Activer mon compte`}
                    <ArrowRight
                      size={16}
                      weight="bold"
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </>
                )}
              </Button>
            </motion.div>
          </motion.div>

          <motion.div {...fadeUp(6)} className="flex items-center justify-center gap-1.5">
            <ShieldCheck size={13} className="shrink-0 text-[#3ecf8e]" />
            <span className="text-[0.72rem] text-gray-400">
              {t`Vos données sont chiffrées et sécurisées.`}
            </span>
          </motion.div>
        </form>
      </Form>
    </>
  );
};
